import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSceneAnchor } from './scene/sceneRegistry';
import { ModelSectionConfig } from './constants';

/**
 * One pinned model chapter (ASR / TTS / SLM). The tall wrapper gives the
 * scroll runway; the glass card stays pinned while the section's being takes
 * focus in the canvas behind it. Cards alternate sides on desktop and sit at
 * the bottom of the viewport on mobile so the orb stays visible above them.
 */
const FEATURES = ['how', 'tuned', 'leverage'] as const;

const ModelSection: React.FC<{ config: ModelSectionConfig }> = ({ config }) => {
  const { t } = useTranslation();
  const anchorRef = useSceneAnchor(config.key);
  const base = `lab.models.${config.key}`;

  return (
    <section id={`lab-${config.key}`} ref={anchorRef} className="relative z-10 h-[175vh]">
      <div className="sticky top-16 flex h-[calc(100dvh-4rem)] items-end px-4 pb-6 md:items-center md:px-[72px] md:pb-0">
        <div
          className={`mx-auto flex w-full max-w-[1280px] justify-center ${
            config.align === 'right' ? 'md:justify-end' : 'md:justify-start'
          }`}
        >
        <div className="lab-glass lab-siri w-full max-w-[470px] rounded-[14px] p-6 md:p-[36px_40px]">
          <div className="font-plexmono text-[11px] tracking-[0.14em]" style={{ color: config.accent }}>
            {config.num} · {t(`${base}.label`)}
          </div>
          <h2 className="mb-3 mt-3.5 text-[26px] font-semibold tracking-[-0.02em] md:text-[32px]">
            {t(`${base}.title`)}
          </h2>
          <p className="mb-4 text-[15px] leading-[1.7] text-lab-frost/70">{t(`${base}.desc`)}</p>
          {/* Shared spec chips: traits all three models have, so the feature rows below can carry only what makes this model unique */}
          <div className="mb-5 flex flex-wrap gap-1.5">
            {(['offline', 'cpu', 'realtime'] as const).map((chip) => (
              <span
                key={chip}
                className="lab-siri rounded-full border border-lab-frost/25 px-2.5 py-1 font-plexmono text-[9.5px] tracking-[0.1em] text-lab-frost/70"
              >
                {t(`lab.models.chips.${chip}`)}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-4 border-t border-white/[0.12] pt-5">
            {FEATURES.map((feature) => (
              <div key={feature}>
                <div
                  className="font-plexmono text-[10.5px] tracking-[0.12em]"
                  style={{ color: config.accent }}
                >
                  {t(`lab.models.labels.${feature}`)}
                </div>
                <div className="mt-[5px] text-[14px] leading-[1.6] text-lab-frost/75">
                  {t(`${base}.${feature}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default ModelSection;
